import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import apiService from './apiService';
import {
  DepositRequest,
  WithdrawRequest,
  CoinDepositRequest,
  CoinWithdrawRequest,
  DepositWithdrawResponse,
  DepositWithdrawHistoryRequest,
  DepositWithdrawHistoryResponse,
  TransactionHistoryRequest,
  PointExchangeRequest,
  PointExchangeResponse,
  PointHistoryRequest,
  PointHistoryResponse,
  RollingHistoryRequest,
  RollingHistoryResponse,
  BonusListResponse,
  ApiResponse,
} from '../types/api';

/** Invalidate all balance-related queries so every component showing balance/wallet refetches */
export function invalidateBalanceQueries(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: ['wallet'] });
  queryClient.invalidateQueries({ queryKey: ['money'] });
  queryClient.invalidateQueries({ queryKey: ['walletInfo'] });
  queryClient.invalidateQueries({ queryKey: ['depositHistory'] });
  queryClient.invalidateQueries({ queryKey: ['transactions'] });
  queryClient.invalidateQueries({ queryKey: ['claimable-bonuses'] });
  queryClient.invalidateQueries({ queryKey: ['available-coupons'] });
  queryClient.invalidateQueries({ queryKey: ['topWageringPlayers'] });
  queryClient.invalidateQueries({ queryKey: ['sports', 'bettingHistory'] });
  queryClient.invalidateQueries({ queryKey: ['minigame', 'bettingHistory'] });
}

// Wallet Info Types
export interface WalletBalance {
  total: number;
  withdrawable: number;
  locked: number;
  bonusLocked?: number;
  lockedWinnings?: number;
  rolling: number;
  lossing: number;
  rakeback: number;
}

export interface WageringInfo {
  total: number;
  completed: number;
  remaining: number;
  progress: number;
}

export interface ActiveBonus {
  id: string;
  name: string;
  type: string;
  amount: number;
  wageringRequired: number;
  wageringCompleted: number;
  wageringRemaining: number;
  progress: number;
  expiresAt: string | null;
  createdAt: string;
}

export interface VIPInfo {
  level: number;
  name: string;
  icon?: string;
  colorFrom?: string;
  colorTo?: string;
  cashbackRate: number;
  rakeback: number;
  nextLevel: {
    level: number;
    name: string;
    icon?: string;
    colorFrom?: string;
    colorTo?: string;
    wagerRequired: number;
    wagerRemaining: number;
    progress: number;
  } | null;
  totalWagered: number;
  compPercent: number;
}

export interface VIPLevel {
  level: number;
  name: string;
  icon: string;
  minWager: number;
  cashbackRate: number;
  rakeback: number;
  colorFrom: string;
  colorTo: string;
}

export interface WalletStats {
  totalWagered: number;
  casinoWagered: number;
  slotWagered: number;
  todayDeposits: number;
  todayWithdrawals: number;
}

export interface RecentTransaction {
  type: string;
  amount: number;
  status: number;
  date: string;
}

export interface MoneyLimits {
  minDeposit: number;
  maxDeposit: number;
  minWithdrawal: number;
  maxWithdrawal: number;
  dailyWithdrawalLimit: number;
  withdrawalFeePercent: number;
}

export interface WalletInfoResponse {
  status: number;
  data: {
    balance: WalletBalance;
    wagering: WageringInfo;
    activeBonuses: ActiveBonus[];
    vip: VIPInfo;
    vipLevels?: VIPLevel[];
    stats: WalletStats;
    recentTransactions: RecentTransaction[];
    limits: MoneyLimits;
  };
  message: { success?: string; warning?: string };
}

// Get comprehensive wallet info
export async function getWalletInfo(): Promise<WalletInfoResponse> {
  try {
    const response = await apiService.postRaw('/api/Money/getWalletInfo', {});
    return response;
  } catch (error) {
    throw error;
  }
}

// Claim cashback
export async function claimCashback(): Promise<ApiResponse> {
  try {
    const response = await apiService.post('/api/Money/claimCashback', {});
    return response;
  } catch (error) {
    throw error;
  }
}

// Claim rakeback
export async function claimRakeback(): Promise<ApiResponse> {
  try {
    const response = await apiService.post('/api/Money/claimRakeback', {});
    return response;
  } catch (error) {
    throw error;
  }
}

// Forfeit bonus
export async function forfeitBonus(bonusId: string): Promise<ApiResponse> {
  try {
    const response = await apiService.post('/api/Money/forfeitBonus', { bonusId });
    return response;
  } catch (error) {
    throw error;
  }
}

// Wallet Info Hook
export function useWalletInfo() {
  return useQuery({
    queryKey: ['wallet', 'info'],
    queryFn: getWalletInfo,
    enabled: apiService.isAuthenticated(),
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 60 * 1000, // Refetch every minute
  });
}

// Claim Cashback Hook
export function useClaimCashback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: claimCashback,
    onSuccess: () => {
      invalidateBalanceQueries(queryClient);
    },
  });
}

// Claim Rakeback Hook
export function useClaimRakeback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: claimRakeback,
    onSuccess: () => {
      invalidateBalanceQueries(queryClient);
    },
  });
}

// Forfeit Bonus Hook
export function useForfeitBonus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: forfeitBonus,
    onSuccess: () => {
      invalidateBalanceQueries(queryClient);
    },
  });
}

// Check payment status (polls NowPayments and auto-approves if finished)
export async function checkPaymentStatus(paymentId: string): Promise<{
  status: number;
  data?: {
    paymentId: string;
    status?: string;
    dbStatus: string;
    isFinished?: boolean;
    isExpired?: boolean;
    justApproved?: boolean;
    creditedAmount?: number;
    providerStatus?: string;
    invoiceUrl?: string;
  };
  message?: { success?: string; warning?: string; info?: string };
}> {
  try {
    // Use sync-payment endpoint which actively syncs with NowPayments and auto-approves
    const response = await apiService.post(`/api/Money/sync-payment/${paymentId}`, {});
    return response;
  } catch (error) {
    throw error;
  }
}

// Force approve payment (for testing when IPN can't reach us)
export async function forceApprovePayment(paymentId: string): Promise<{
  status: number;
  data?: {
    paymentId: string;
    creditedAmount?: number;
    newBalance?: number;
    justApproved?: boolean;
    alreadyCompleted?: boolean;
  };
  message?: { success?: string; warning?: string };
}> {
  try {
    const response = await apiService.post(`/api/Money/force-approve/${paymentId}`, {});
    return response;
  } catch (error) {
    throw error;
  }
}

// Deposit/Withdraw Services
export async function getExchangeInfo(
  amount: number,
  typeStr: 'deposit' | 'withdraw',
  withdrawPassword?: string,
  bonus?: string
): Promise<ApiResponse> {
  try {
    const payload = await apiService.addCommonData({
      amount,
      typeStr,
      withdrawPassword,
      bonus,
    });
    const response = await apiService.post('/api/Money/getExchangeInfo', payload);
    return response;
  } catch (error) {
    throw error;
  }
}

export async function doDeposit(data: DepositRequest): Promise<DepositWithdrawResponse> {
  try {
    const payload = await apiService.addCommonData(data);
    const response = await apiService.postRaw('/api/Money/coinExchange', payload);
    return response;
  } catch (error) {
    throw error;
  }
}

export async function doWithdraw(data: WithdrawRequest): Promise<DepositWithdrawResponse> {
  try {
    const payload = await apiService.addCommonData(data);
    const response = await apiService.postRaw('/api/Money/coinExchange', payload);
    return response;
  } catch (error) {
    throw error;
  }
}

export async function doCoinDeposit(data: CoinDepositRequest): Promise<DepositWithdrawResponse> {
  try {
    const payload = await apiService.addCommonData(data);
    const response = await apiService.postRaw('/api/Money/coinExchange', payload);
    return response;
  } catch (error) {
    throw error;
  }
}

export async function doCoinWithdraw(data: CoinWithdrawRequest): Promise<DepositWithdrawResponse> {
  try {
    const payload = await apiService.addCommonData(data);
    const response = await apiService.postRaw('/api/Money/coinExchange', payload);
    return response;
  } catch (error) {
    throw error;
  }
}

export async function getDepositHistory(params: DepositWithdrawHistoryRequest): Promise<DepositWithdrawHistoryResponse> {
  try {
    const payload = await apiService.addCommonData(params);
    const response = await apiService.postRaw('/api/Money/getHistory', payload);
    return response;
  } catch (error) {
    throw error;
  }
}

export async function getCoinDepositHistory(params: DepositWithdrawHistoryRequest): Promise<DepositWithdrawHistoryResponse> {
  try {
    const payload = await apiService.addCommonData(params);
    const response = await apiService.postRaw('/api/Money/getCoinHistory', payload);
    return response;
  } catch (error) {
    throw error;
  }
}

export async function getRoyceDepositHistory(params: DepositWithdrawHistoryRequest): Promise<DepositWithdrawHistoryResponse> {
  try {
    const payload = await apiService.addCommonData(params);
    const response = await apiService.postRaw('/api/Money/getRoyceHistory', payload);
    return response;
  } catch (error) {
    throw error;
  }
}

export async function getWithdrawHistory(params: DepositWithdrawHistoryRequest): Promise<DepositWithdrawHistoryResponse> {
  try {
    const payload = await apiService.addCommonData(params);
    const response = await apiService.postRaw('/api/Money/getHistory', payload);
    return response;
  } catch (error) {
    throw error;
  }
}

export async function getTransactionHistory(params: TransactionHistoryRequest): Promise<DepositWithdrawHistoryResponse> {
  try {
    const payload = await apiService.addCommonData(params);
    const response = await apiService.postRaw('/api/Money/getHistory', payload);
    return response;
  } catch (error) {
    throw error;
  }
}

export async function deleteDepositHistory(data: { id: string; typeStr: string }): Promise<ApiResponse> {
  try {
    const response = await apiService.post('/api/Money/deleteHistory', data);
    return response;
  } catch (error) {
    throw error;
  }
}

// Point Exchange Services
export async function doPointExchange(data: PointExchangeRequest): Promise<PointExchangeResponse> {
  try {
    const payload = await apiService.addCommonData(data);
    const response = await apiService.postRaw('/api/Money/rollingChange', payload, {
      headers: {
        'Authorization': localStorage.getItem('authToken'),
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
}

export async function getPointHistory(params: PointHistoryRequest): Promise<PointHistoryResponse> {
  try {
    const payload = await apiService.addCommonData(params);
    const response = await apiService.postRaw('/api/Money/getPointHistory', payload);
    return response;
  } catch (error) {
    throw error;
  }
}

export async function getRollingHistory(params: RollingHistoryRequest): Promise<RollingHistoryResponse> {
  try {
    const payload = await apiService.addCommonData(params);
    const response = await apiService.postRaw('/api/Money/getRollingHistory', payload);
    return response;
  } catch (error) {
    throw error;
  }
}

export async function deleteRollingHistory(data: { id: string; type: string }): Promise<ApiResponse> {
  try {
    const response = await apiService.post('/api/Money/deleteHistory', data);
    return response;
  } catch (error) {
    throw error;
  }
}

// Bonus Services
export async function getBonusList(): Promise<BonusListResponse> {
  try {
    const response = await apiService.postRaw('/api/Money/getBonusList', {});
    return response;
  } catch (error) {
    throw error;
  }
}

// React Query Hooks
export function useExchangeInfo() {
  return useMutation({
    mutationFn: ({ amount, typeStr, withdrawPassword, bonus }: {
      amount: number;
      typeStr: 'deposit' | 'withdraw';
      withdrawPassword?: string;
      bonus?: string;
    }) => getExchangeInfo(amount, typeStr, withdrawPassword, bonus),
  });
}

export function useDeposit() {
  return useMutation({
    mutationFn: doDeposit,
    onSuccess: () => {
      // Refetch user info and deposit history after successful deposit
      // You might want to invalidate related queries here
    },
  });
}

export function useWithdraw() {
  return useMutation({
    mutationFn: doWithdraw,
    onSuccess: () => {
      // Refetch user info and withdraw history after successful withdraw
      // You might want to invalidate related queries here
    },
  });
}

export function useCoinDeposit() {
  return useMutation({
    mutationFn: doCoinDeposit,
    onSuccess: () => {
      // Refetch user info and deposit history after successful coin deposit
      // You might want to invalidate related queries here
    },
  });
}

export function useCoinWithdraw() {
  return useMutation({
    mutationFn: doCoinWithdraw,
    onSuccess: () => {
      // Refetch user info and withdraw history after successful coin withdraw
      // You might want to invalidate related queries here
    },
  });
}

export function useDepositHistory(typeStr: 'deposit' | 'withdraw', page: number = 1, perPage: number = 10) {
  return useQuery({
    queryKey: ['money', 'depositHistory', typeStr, page, perPage],
    queryFn: () => getDepositHistory({ typeStr, page, perPage }),
    enabled: apiService.isAuthenticated(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

export function useCoinDepositHistory(typeStr: 'deposit' | 'withdraw', page: number = 1, perPage: number = 10) {
  return useQuery({
    queryKey: ['money', 'coinDepositHistory', typeStr, page, perPage],
    queryFn: () => getCoinDepositHistory({ typeStr, page, perPage }),
    enabled: apiService.isAuthenticated(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

export function useRoyceDepositHistory(typeStr: 'deposit' | 'withdraw', page: number = 1, perPage: number = 10) {
  return useQuery({
    queryKey: ['money', 'royceDepositHistory', typeStr, page, perPage],
    queryFn: () => getRoyceDepositHistory({ typeStr, page, perPage }),
    enabled: apiService.isAuthenticated(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

export function useWithdrawHistory(typeStr: 'deposit' | 'withdraw', page: number = 1, perPage: number = 10) {
  return useQuery({
    queryKey: ['money', 'withdrawHistory', typeStr, page, perPage],
    queryFn: () => getWithdrawHistory({ typeStr, page, perPage }),
    enabled: apiService.isAuthenticated(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

export function useTransactionHistory(typeStr: 'deposit' | 'withdraw' | 'bet' | 'all', page: number = 1, perPage: number = 10) {
  return useQuery({
    queryKey: ['money', 'transactionHistory', typeStr, page, perPage],
    queryFn: () => getTransactionHistory({ typeStr, page, perPage }),
    enabled: apiService.isAuthenticated(),
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

export function usePointExchange() {
  return useMutation({
    mutationFn: doPointExchange,
    onSuccess: () => {
      // Refetch user info and point history after successful exchange
      // You might want to invalidate related queries here
    },
  });
}

export function usePointHistory(page: number = 1, perPage: number = 10) {
  return useQuery({
    queryKey: ['money', 'pointHistory', page, perPage],
    queryFn: () => getPointHistory({ page, perPage }),
    enabled: apiService.isAuthenticated(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

export function useRollingHistory(page: number = 1, perPage: number = 10) {
  return useQuery({
    queryKey: ['money', 'rollingHistory', page, perPage],
    queryFn: () => getRollingHistory({ page, perPage }),
    enabled: apiService.isAuthenticated(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

export function useBonusList() {
  return useQuery({
    queryKey: ['money', 'bonusList'],
    queryFn: getBonusList,
    enabled: apiService.isAuthenticated(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  });
}

export function useDeleteDepositHistory() {
  return useMutation({
    mutationFn: deleteDepositHistory,
    onSuccess: () => {
      // Refetch deposit history after successful deletion
      // You might want to invalidate related queries here
    },
  });
}

export function useDeleteRollingHistory() {
  return useMutation({
    mutationFn: deleteRollingHistory,
    onSuccess: () => {
      // Refetch rolling history after successful deletion
      // You might want to invalidate related queries here
    },
  });
}

// Chat Eligibility Types
export interface ChatEligibilityRequirement {
  required: number;
  current: number;
  progress: number;
  met: boolean;
}

export interface ChatEligibilityResponse {
  status: number;
  data: {
    isEligible: boolean;
    requirements: {
      deposit: ChatEligibilityRequirement;
      wager: ChatEligibilityRequirement;
    };
    message: string;
  };
}

export interface ClaimChatBonusResponse {
  status: number;
  data: {
    claimed: boolean;
    message: string;
  };
}

// Chat Eligibility API
export async function getChatEligibility(): Promise<ChatEligibilityResponse> {
  try {
    const response = await apiService.get('/api/Money/chat-eligibility');
    return response as ChatEligibilityResponse;
  } catch (error) {
    throw error;
  }
}

export async function claimChatBonus(): Promise<ClaimChatBonusResponse> {
  try {
    const response = await apiService.post('/api/Money/claim-chat-bonus', {});
    return response as ClaimChatBonusResponse;
  } catch (error) {
    throw error;
  }
}

export function useChatEligibility() {
  return useQuery({
    queryKey: ['money', 'chatEligibility'],
    queryFn: getChatEligibility,
    enabled: apiService.isAuthenticated(),
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useClaimChatBonus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: claimChatBonus,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['money', 'chatEligibility'] });
    },
  });
}

// Daily Bonus: rolling 7 days, wager $50 (same as chat; no permanent claim)
export interface DailyBonusEligibilityResponse {
  status: number;
  data: {
    isEligible: boolean;
    requirements: {
      wager: ChatEligibilityRequirement;
    };
    message: string;
  };
}

export interface ClaimDailyBonusUnlockResponse {
  status: number;
  data: {
    claimed: boolean;
    message: string;
  };
}

// Daily Bonus Unlock API
export async function getDailyBonusEligibility(): Promise<DailyBonusEligibilityResponse> {
  try {
    const response = await apiService.get('/api/Money/daily-bonus-eligibility');
    return response as DailyBonusEligibilityResponse;
  } catch (error) {
    throw error;
  }
}

export async function claimDailyBonusUnlock(): Promise<ClaimDailyBonusUnlockResponse> {
  try {
    const response = await apiService.post('/api/Money/claim-daily-bonus-unlock', {});
    return response as ClaimDailyBonusUnlockResponse;
  } catch (error) {
    throw error;
  }
}

export function useDailyBonusEligibility() {
  return useQuery({
    queryKey: ['money', 'dailyBonusEligibility'],
    queryFn: getDailyBonusEligibility,
    enabled: apiService.isAuthenticated(),
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
  });
}

export function useClaimDailyBonusUnlock() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: claimDailyBonusUnlock,
    onSuccess: () => {
      invalidateBalanceQueries(queryClient);
    },
  });
}

// Birthday Bonus Types
export interface BirthdayBonusEligibilityResponse {
  status: number;
  data: {
    hasBirthday: boolean;
    birthday: string | null;
    isBirthdayToday: boolean;
    alreadyClaimedThisYear: boolean;
    canClaim: boolean;
    requirementsMet: boolean;
    bonusAmount: number;
    vipLevel: number;
    vipName: string;
    requirements: {
      wager: {
        required: number;
        current: number;
        met: boolean;
        progress: number;
      };
    };
    message: string;
  };
}

export interface ClaimBirthdayBonusResponse {
  status: number;
  data: {
    claimed: boolean;
    amount?: number;
    message: string;
  };
}

// Birthday Bonus API
export async function getBirthdayBonusEligibility(): Promise<BirthdayBonusEligibilityResponse> {
  try {
    const response = await apiService.get('/api/Money/birthday-bonus-eligibility');
    return response as BirthdayBonusEligibilityResponse;
  } catch (error) {
    throw error;
  }
}

export async function claimBirthdayBonus(): Promise<ClaimBirthdayBonusResponse> {
  try {
    const response = await apiService.post('/api/Money/claim-birthday-bonus', {});
    return response as ClaimBirthdayBonusResponse;
  } catch (error) {
    throw error;
  }
}

export function useBirthdayBonusEligibility() {
  return useQuery({
    queryKey: ['money', 'birthdayBonusEligibility'],
    queryFn: getBirthdayBonusEligibility,
    enabled: apiService.isAuthenticated(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: true,
  });
}

export function useClaimBirthdayBonus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: claimBirthdayBonus,
    onSuccess: () => {
      invalidateBalanceQueries(queryClient);
    },
  });
}